import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import OutboxIcon from '@mui/icons-material/Outbox';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import dayjs from 'dayjs';
import { useEmail } from '../../../../hooks/useEmail';
import TextInputFeatures from '../../../emails/TextInputFeatures';
import TemplateSelectionModal from '../../../emails/TemplateSelectionModal';
import { toast } from 'react-toastify';
import { getRenderableEmailHtml } from '../../../../util/emailContent';

const CommunicationHistoryModal = ({ open, onClose, booking }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedMail, setSelectedMail] = useState(null);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [mailPreviewOpen, setMailPreviewOpen] = useState(false);
  const [bookingEmails, setBookingEmails] = useState([]);
  const [sendingReply, setSendingReply] = useState(false);
  const { fetchEmailsByInternalBookingId, sendReply } = useEmail();
  const replyFieldRef = useRef(null);
  const [defaultText, setDefaultText] = useState('');

  useEffect(() => {
    const loadBookingEmails = async () => {
      if (booking?._id) {
        try {
          const response = await fetchEmailsByInternalBookingId(booking._id);
          if (response?.mails) {
            setBookingEmails(response.mails);
          }
        } catch (error) {
          toast.error('Fehler beim Laden der E-Mails');
          console.error('Error fetching booking emails:', error);
        }
      }
    };

    loadBookingEmails();
  }, [booking?._id]);

  useEffect(() => {
    if (selectedMail) {
      const fromName =
        selectedMail.from?.split(' <')[0] || selectedMail.toMail || selectedMail.to || 'Kunde';
      const greeting = `Hallo ${fromName},\n\n`;
      const signature = '\n\nMit freundlichen Grüßen\ndein Funzone Team';
      setDefaultText(greeting + signature);
    }
  }, [selectedMail]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleMailPreview = mail => {
    setSelectedMail(mail);
    setMailPreviewOpen(true);
  };

  const handleSendReply = async () => {
    if (!selectedMail || !replyFieldRef.current) return;

    const replyContent = replyFieldRef.current.value;
    setSendingReply(true);
    try {
      await sendReply(selectedMail._id, replyContent);

      // Refresh the email thread
      const refreshResponse = await fetchEmailsByInternalBookingId(booking._id);
      if (refreshResponse?.mails) {
        setBookingEmails(refreshResponse.mails);
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Fehler beim Senden der E-Mail');
    } finally {
      setSendingReply(false);
      setMailPreviewOpen(false);
    }
  };

  // Helper function to get the correct date based on email direction
  const getDate = email => {
    if (email.direction === 'outbound' || email.direction !== 'inbound') {
      return new Date(
        email.sentAt || email.deliveryStatus?.sentAt || email.meta?.createdAt || new Date()
      );
    }
    return new Date(email.receivedAt || email.meta?.createdAt || new Date());
  };

  // Combine and sort all emails
  const allEmails = React.useMemo(() => {
    const emails = [...(booking.mailSent || []), ...bookingEmails];

    // Remove duplicates based on _id
    const uniqueEmails = Array.from(new Map(emails.map(email => [email._id, email])).values());

    // Sort by date in descending order
    return uniqueEmails.sort((a, b) => {
      const dateA = getDate(a);
      const dateB = getDate(b);
      return dateB - dateA;
    });
  }, [booking.mailSent, bookingEmails]);

  const renderContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Typ</TableCell>
                  <TableCell>Datum</TableCell>
                  <TableCell>Betreff</TableCell>
                  <TableCell>Empfänger</TableCell>
                  <TableCell>Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allEmails.map(mail => {
                  const isOutbound = !mail.direction || mail.direction === 'outbound';

                  return (
                    <TableRow key={mail._id}>
                      <TableCell>
                        {isOutbound ? (
                          <OutboxIcon color='primary' />
                        ) : (
                          <MoveToInboxIcon color='success' />
                        )}
                      </TableCell>
                      <TableCell>{dayjs(getDate(mail)).format('DD.MM.YYYY HH:mm')}</TableCell>
                      <TableCell>{mail.subject}</TableCell>
                      <TableCell>{mail.toMail || mail.to}</TableCell>
                      <TableCell>
                        <Tooltip title='E-Mail anzeigen'>
                          <IconButton size='small' onClick={() => handleMailPreview(mail)}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        );
      case 1:
        return (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Datum</TableCell>
                  <TableCell>Typ</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Empfänger</TableCell>
                  <TableCell>Nachricht</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {booking.smsSent.map(sms => (
                  <TableRow key={sms._id}>
                    <TableCell>{dayjs(sms.sendAt).format('DD.MM.YYYY HH:mm')}</TableCell>
                    <TableCell>{sms.type}</TableCell>
                    <TableCell>{sms.externalStatus}</TableCell>
                    <TableCell>{sms.toNumber}</TableCell>
                    <TableCell>{sms.text}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );
      case 2:
        return (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Geplant für</TableCell>
                  <TableCell>Typ</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {booking.smsReminders.map(reminder => (
                  <TableRow key={reminder._id}>
                    <TableCell>{dayjs(reminder.plannedAt).format('DD.MM.YYYY HH:mm')}</TableCell>
                    <TableCell>{reminder.type}</TableCell>
                    <TableCell>{reminder.status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
        <DialogTitle>Kommunikationsverlauf</DialogTitle>
        <DialogContent>
          <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
            <Tab label='E-Mail-Verlauf' />
            <Tab label='SMS-Verlauf' />
            <Tab label='Geplante SMS' />
          </Tabs>
          {renderContent()}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Schließen</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={mailPreviewOpen}
        onClose={() => setMailPreviewOpen(false)}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>{selectedMail?.subject}</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant='body2' color='text.secondary'>
              <b>An:</b> {selectedMail?.toMail || selectedMail?.to}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              <b>Gesendet am:</b>{' '}
              {selectedMail && dayjs(getDate(selectedMail)).format('DD.MM.YYYY HH:mm')}
            </Typography>
          </Box>
          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              p: 2,
              maxHeight: '70vh',
              overflow: 'auto',
            }}
            dangerouslySetInnerHTML={{
              __html: getRenderableEmailHtml(selectedMail),
            }}
          />
          {selectedMail?.direction === 'inbound' && (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                multiline
                rows={10}
                inputRef={replyFieldRef}
                defaultValue={defaultText}
                placeholder='Deine Antwort...'
                disabled={sendingReply}
                key={selectedMail?._id}
              />
              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <TextInputFeatures
                  onTemplateSelect={() => setTemplateModalOpen(true)}
                  textareaRef={replyFieldRef}
                />
                <Button
                  variant='contained'
                  color='success'
                  onClick={handleSendReply}
                  disabled={sendingReply}
                  startIcon={
                    sendingReply ? <CircularProgress size={20} color='inherit' /> : <SendIcon />
                  }
                >
                  {sendingReply ? 'Wird gesendet...' : 'Senden'}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMailPreviewOpen(false)}>Schließen</Button>
        </DialogActions>
        <TemplateSelectionModal
          open={templateModalOpen}
          onClose={() => setTemplateModalOpen(false)}
          onSelectTemplate={content => {
            if (replyFieldRef.current) {
              replyFieldRef.current.value = content;
            }
            setTemplateModalOpen(false);
          }}
          selectedEmail={selectedMail}
        />
      </Dialog>
    </>
  );
};

export default CommunicationHistoryModal;
